import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
} from '@mui/material';
import {
  ArrowBack,
  TrendingUp,
  TrendingDown,
  Schedule,
  Person,
  Download,
  Print,
  Email,
  Star,
  Warning,
  CheckCircle,
  Timer,
  Assignment,
  Quiz,
  EmojiEvents,
} from '@mui/icons-material';

interface Child {
  id: number;
  name: string;
  avatar: string;
  grade: string;
}

interface ProgressData {
  childId: number;
  childName: string;
  overallProgress: number;
  weeklyProgress: number;
  monthlyProgress: number;
  subjects: {
    name: string;
    progress: number;
    averageScore: number;
    timeSpent: number;
    trend: 'up' | 'down' | 'stable';
    lastActivity: Date;
    status: 'excellent' | 'good' | 'needs_attention' | 'concerning';
  }[];
  weeklyStats: {
    week: string;
    hoursStudied: number;
    lessonsCompleted: number;
    averageScore: number;
    activeDays: number;
  }[];
  strengths: string[];
  areasForImprovement: string[];
  recommendations: string[];
  upcomingDeadlines: {
    course: string;
    assignment: string;
    dueDate: Date;
    priority: 'high' | 'medium' | 'low';
  }[];
}

const ParentProgressReports: React.FC = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState<Child[]>([]);
  const [progressData, setProgressData] = useState<ProgressData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState<number | 'all'>('all');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('monthly');
  const [activeTab, setActiveTab] = useState<{[key: number]: number}>({});

  useEffect(() => {
    // Mock data - replace with API calls
    setLoading(true);
    setTimeout(() => {
      const mockChildren: Child[] = [
        { id: 1, name: 'Emma Johnson', avatar: 'E', grade: '6th Grade' },
        { id: 2, name: 'Lucas Johnson', avatar: 'L', grade: '3rd Grade' },
      ];

      const mockProgressData: ProgressData[] = [
        {
          childId: 1,
          childName: 'Emma Johnson',
          overallProgress: 78,
          weeklyProgress: 85,
          monthlyProgress: 82,
          subjects: [
            {
              name: 'Mathematics',
              progress: 85,
              averageScore: 92,
              timeSpent: 180, // minutes this week
              trend: 'up',
              lastActivity: new Date(Date.now() - 2 * 60 * 60 * 1000),
              status: 'excellent'
            },
            {
              name: 'English',
              progress: 65,
              averageScore: 78,
              timeSpent: 120,
              trend: 'stable',
              lastActivity: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
              status: 'needs_attention'
            },
            {
              name: 'Science',
              progress: 90,
              averageScore: 95,
              timeSpent: 150,
              trend: 'up',
              lastActivity: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
              status: 'excellent'
            }
          ],
          weeklyStats: [
            { week: 'Week 1', hoursStudied: 8.5, lessonsCompleted: 12, averageScore: 88, activeDays: 5 },
            { week: 'Week 2', hoursStudied: 7.2, lessonsCompleted: 10, averageScore: 85, activeDays: 4 },
            { week: 'Week 3', hoursStudied: 9.1, lessonsCompleted: 14, averageScore: 91, activeDays: 6 },
            { week: 'Week 4', hoursStudied: 8.8, lessonsCompleted: 13, averageScore: 89, activeDays: 5 },
          ],
          strengths: [
            'Excellent problem-solving skills in mathematics',
            'Consistent daily learning habits',
            'Strong performance in STEM subjects'
          ],
          areasForImprovement: [
            'Reading comprehension needs more practice',
            'Writing skills require development',
            'Time management during tests'
          ],
          recommendations: [
            'Increase daily reading time to 30 minutes',
            'Practice essay writing weekly',
            'Use timer during practice tests',
            'Consider joining math competition team'
          ],
          upcomingDeadlines: [
            {
              course: 'Algebra Fundamentals',
              assignment: 'Linear Equations Test',
              dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
              priority: 'high'
            },
            {
              course: 'Reading Comprehension',
              assignment: 'Book Report',
              dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
              priority: 'medium'
            }
          ]
        },
        {
          childId: 2,
          childName: 'Lucas Johnson',
          overallProgress: 72,
          weeklyProgress: 68,
          monthlyProgress: 75,
          subjects: [
            {
              name: 'Programming',
              progress: 70,
              averageScore: 82,
              timeSpent: 90,
              trend: 'up',
              lastActivity: new Date(Date.now() - 3 * 60 * 60 * 1000),
              status: 'good'
            },
            {
              name: 'History',
              progress: 55,
              averageScore: 75,
              timeSpent: 60,
              trend: 'down',
              lastActivity: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
              status: 'needs_attention'
            }
          ],
          weeklyStats: [
            { week: 'Week 1', hoursStudied: 5.5, lessonsCompleted: 8, averageScore: 78, activeDays: 4 },
            { week: 'Week 2', hoursStudied: 6.2, lessonsCompleted: 9, averageScore: 82, activeDays: 5 },
            { week: 'Week 3', hoursStudied: 4.8, lessonsCompleted: 7, averageScore: 75, activeDays: 3 },
            { week: 'Week 4', hoursStudied: 6.0, lessonsCompleted: 8, averageScore: 80, activeDays: 4 },
          ],
          strengths: [
            'Creative problem-solving approach',
            'Enjoys hands-on learning activities',
            'Good collaboration skills'
          ],
          areasForImprovement: [
            'Consistency in daily study routine',
            'Focus during longer lessons',
            'Historical fact memorization'
          ],
          recommendations: [
            'Set up a structured daily schedule',
            'Use visual aids for history lessons',
            'Take breaks every 20 minutes during study',
            'Reward completion of weekly goals'
          ],
          upcomingDeadlines: [
            {
              course: 'Introduction to Programming',
              assignment: 'Scratch Project',
              dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
              priority: 'medium'
            }
          ]
        }
      ];

      setChildren(mockChildren);
      setProgressData(mockProgressData);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'success';
      case 'good':
        return 'info';
      case 'needs_attention':
        return 'warning';
      case 'concerning':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'excellent':
        return <CheckCircle color="success" />;
      case 'good':
        return <Star color="info" />;
      case 'needs_attention':
        return <Warning color="warning" />;
      case 'concerning':
        return <Warning color="error" />;
      default:
        return <CheckCircle />;
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp color="success" />;
      case 'down':
        return <TrendingDown color="error" />;
      case 'stable':
        return <TrendingUp color="disabled" />;
      default:
        return <TrendingUp color="disabled" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const filteredData = selectedChild === 'all' 
    ? progressData 
    : progressData.filter(data => data.childId === selectedChild);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/parent-dashboard')}
            sx={{ cursor: 'pointer' }}
          >
            Parent Dashboard
          </Link>
          <Typography color="text.primary">Progress Reports</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/parent-dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Progress Reports
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Detailed analysis of your children's learning progress
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Download />}>
            Export PDF
          </Button>
          <Button variant="outlined" startIcon={<Email />}>
            Email Report
          </Button>
          <Button variant="outlined" startIcon={<Print />}>
            Print
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Child</InputLabel>
              <Select
                value={selectedChild}
                label="Child"
                onChange={(e) => setSelectedChild(e.target.value as number | 'all')}
              >
                <MenuItem value="all">All Children</MenuItem>
                {children.map((child) => (
                  <MenuItem key={child.id} value={child.id}>
                    {child.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={selectedPeriod}
                label="Time Period"
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <MenuItem value="weekly">This Week</MenuItem>
                <MenuItem value="monthly">This Month</MenuItem>
                <MenuItem value="quarterly">This Quarter</MenuItem>
                <MenuItem value="yearly">This Year</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Reports for Each Child */}
      {filteredData.map((data) => (
        <Paper key={data.childId} sx={{ mb: 4 }}>
          {/* Child Header */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                {data.childName.charAt(0)}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {data.childName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Overall Progress: {data.overallProgress}%
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => navigate(`/parent-child-dashboard/${data.childId}`)}
              >
                View Full Dashboard
              </Button>
            </Box>
          </Box>

          {/* Tabs */}
          <Tabs value={activeTab[data.childId] || 0} onChange={(e, newValue) => setActiveTab({...activeTab, [data.childId]: newValue})}>
            <Tab label="Course Performance" />
            <Tab label="Weekly Trends" />
            <Tab label="Insights & Recommendations" />
            <Tab label="Upcoming Deadlines" />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ p: 3 }}>
            {(activeTab[data.childId] || 0) === 0 && (
              <Grid container spacing={3}>
                {data.subjects.map((subject, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {subject.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getTrendIcon(subject.trend)}
                            {getStatusIcon(subject.status)}
                          </Box>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Progress
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {subject.progress}%
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={subject.progress} 
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Average Score
                            </Typography>
                            <Typography variant="h6" color="primary">
                              {subject.averageScore}%
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Time This Week
                            </Typography>
                            <Typography variant="h6" color="secondary">
                              {formatTime(subject.timeSpent)}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Box sx={{ mt: 2 }}>
                          <Chip 
                            label={subject.status.replace('_', ' ')} 
                            color={getStatusColor(subject.status) as any}
                            size="small" 
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                            Last activity: {subject.lastActivity.toLocaleDateString()}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {(activeTab[data.childId] || 0) === 1 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Week</TableCell>
                      <TableCell>Hours Studied</TableCell>
                      <TableCell>Lessons Completed</TableCell>
                      <TableCell>Average Score</TableCell>
                      <TableCell>Active Days</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.weeklyStats.map((week, index) => (
                      <TableRow key={index}>
                        <TableCell>{week.week}</TableCell>
                        <TableCell>{week.hoursStudied}h</TableCell>
                        <TableCell>{week.lessonsCompleted}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {week.averageScore}%
                            <Chip 
                              label={week.averageScore >= 85 ? 'Excellent' : week.averageScore >= 70 ? 'Good' : 'Needs Improvement'}
                              color={week.averageScore >= 85 ? 'success' : week.averageScore >= 70 ? 'info' : 'warning'}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>{week.activeDays}/7 days</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {(activeTab[data.childId] || 0) === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                        Strengths
                      </Typography>
                      {data.strengths.map((strength, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main', mt: 0.5 }} />
                          <Typography variant="body2">{strength}</Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}>
                        Areas for Improvement
                      </Typography>
                      {data.areasForImprovement.map((area, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                          <Warning sx={{ fontSize: 16, color: 'warning.main', mt: 0.5 }} />
                          <Typography variant="body2">{area}</Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'info.main' }}>
                        Recommendations
                      </Typography>
                      {data.recommendations.map((recommendation, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                          <Star sx={{ fontSize: 16, color: 'info.main', mt: 0.5 }} />
                          <Typography variant="body2">{recommendation}</Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {(activeTab[data.childId] || 0) === 3 && (
              <Box>
                {data.upcomingDeadlines.length === 0 ? (
                  <Alert severity="info">No upcoming deadlines for this child.</Alert>
                ) : (
                  <Grid container spacing={2}>
                    {data.upcomingDeadlines.map((deadline, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Card>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {deadline.assignment}
                              </Typography>
                              <Chip 
                                label={deadline.priority} 
                                color={getPriorityColor(deadline.priority) as any}
                                size="small" 
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Course: {deadline.course}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Schedule color="primary" />
                              <Typography variant="body2">
                                Due: {deadline.dueDate.toLocaleDateString()}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      ))}
    </Container>
  );
};

export default ParentProgressReports;
